import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { EquipoHilo, EquipoMensaje } from "../../../services/v2/prospects.service";
import WappIcono from "../../../components/wappIcono";
import { useFotos } from "../../../appMovil/fotos";
import { cuando, diaEtiqueta, errorTexto, hora, iniciales } from "./salesHelpers";
import { MedioMensaje, PastillaReacciones } from "./equipoMedios";
import { BotonEmoji, insertarEnCursor, teclaEnter } from "./equipoEmojis";
import { idsMencionados, ListaMenciones, Persona, primerNombre, useMenciones } from "./menciones";
import { useCrecer } from "./crecer";
import { copiarTexto } from "./seleccion";
import { adjuntoCorto, resumenTexto } from "./equipoGifs";
import { EMOJIS, Texto } from "./equipoView";

const http = HttpManager.getInstance();

/** Foto o iniciales de quien escribe; al tocarla se abre su tarjeta de perfil. */
export const FotoPersona: React.FC<{ id: number; nombre: string; fotos: Record<number, string>; className?: string; onClick?: (e: React.MouseEvent) => void }> = ({ id, nombre, fotos, className, onClick }) => {
	const p = nombre.split(" ");
	return (
		<button type="button" tabIndex={-1} className={"eq-av tono-" + (Math.abs(id || nombre.length) % 6) + (fotos[id] ? " con-foto" : "") + (className ? " " + className : "")}
			onClick={onClick} aria-label={"Perfil de " + nombre}>
			{fotos[id] ? <img src={fotos[id]} alt="" loading="lazy" /> : iniciales({ first_name: p[0], last_name: p[1] || "" })}
		</button>
	);
};

/** "3 respuestas · Última hoy 4:12 pm" con las fotitos de quienes respondieron, debajo del mensaje de arriba. */
export const ResumenHilo: React.FC<{ m: EquipoMensaje; fotos: Record<number, string>; nuevas?: number; onAbrir: () => void }> = ({ m, fotos, nuevas, onAbrir }) => {
	if (!m.hilo_n) return null;
	return (
		<button type="button" className={"eq-hilo-res" + (nuevas ? " nuevas" : "")} onClick={(e) => { e.stopPropagation(); onAbrir(); }}>
			<span className="eq-hilo-caras" aria-hidden="true">
				{(m.hilo_gente || []).map((u) => <span key={u} className={"eq-hilo-cara tono-" + (Math.abs(u) % 6)}>{fotos[u] ? <img src={fotos[u]} alt="" loading="lazy" /> : null}</span>)}
			</span>
			<b>{m.hilo_n} {m.hilo_n === 1 ? "respuesta" : "respuestas"}</b>
			<span className="eq-hilo-cuando">{nuevas ? nuevas + (nuevas === 1 ? " nueva" : " nuevas") : m.hilo_ultimo_at ? "Última " + cuando(m.hilo_ultimo_at) : ""}</span>
			<span className="eq-hilo-ver">Ver hilo ›</span>
		</button>
	);
};

interface Props {
	campusId: number;
	hiloId: number;
	/** Nombre de la conversación ("# Team Kits", "Helen Velasquez"). */
	donde: string;
	puedoEscribir: boolean;
	yoId: number | null;
	/** Sube cada que llega un aviso de este hilo (push): se vuelve a pedir. */
	version: number;
	onCerrar: () => void;
	onPerfil: (userId: number, e: React.MouseEvent) => void;
	/** Después de responder o reaccionar: la conversación vuelve a pedir el contador del hilo. */
	onCambio: () => void;
}

/**
 * Panel del hilo, como Slack (Esteban, 2026-10-25): el mensaje de arriba, sus respuestas y una caja para responder con
 * "También mandar a la conversación". En computadora va a la derecha de la conversación; en celular, a pantalla completa.
 */
const HiloPanel: React.FC<Props> = ({ campusId, hiloId, donde, puedoEscribir, yoId, version, onCerrar, onPerfil, onCambio }) => {
	const fotos = useFotos();
	const [h, setH] = useState<EquipoHilo | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [texto, setTexto] = useState("");
	const [tambien, setTambien] = useState(false);
	const [enviando, setEnviando] = useState(false);
	const [menciones, setMenciones] = useState<{ id: number; nombre: string }[]>([]);
	const [reac, setReac] = useState<number | null>(null);
	const caja = useRef<HTMLTextAreaElement>(null);
	const fin = useRef<HTMLDivElement>(null);
	const lista = useRef<HTMLDivElement>(null);
	const canalId = h?.canalId ?? null;
	const men = useMenciones(campusId, canalId);
	useCrecer(caja, texto, 8);

	const cargar = useCallback(async (bajar: boolean) => {
		try {
			const r = await http.prospectsService.equipoHilo(campusId, hiloId);
			const cerca = !lista.current || lista.current.scrollHeight - lista.current.scrollTop - lista.current.clientHeight < 120;
			setH(r.data);
			setError(null);
			if (bajar || cerca) setTimeout(() => fin.current?.scrollIntoView({ block: "end" }), 30);
		} catch (e) { setError(errorTexto(e)); }
	}, [campusId, hiloId]);

	useEffect(() => { setH(null); setTexto(""); setTambien(false); setMenciones([]); cargar(true); setTimeout(() => caja.current?.focus(), 60); }, [hiloId, cargar]);
	useEffect(() => { if (version) cargar(false); }, [version, cargar]);
	useEffect(() => {
		const tecla = (e: KeyboardEvent) => { if (e.key === "Escape" && document.activeElement !== caja.current) onCerrar(); };
		window.addEventListener("keydown", tecla);
		return () => window.removeEventListener("keydown", tecla);
	}, [onCerrar]);

	const enviar = async () => {
		const t = texto.trim();
		if (!t || !canalId || enviando) return;
		setEnviando(true);
		try {
			await http.prospectsService.equipoEnviar(campusId, canalId, t, null, { hiloId, tambien, menciones: idsMencionados(menciones, t) });
			setTexto("");
			setMenciones([]);
			setTambien(false);
			await cargar(true);
			onCambio();
		} catch (e) { setError(errorTexto(e)); }
		setEnviando(false);
		caja.current?.focus();
	};
	const reaccionar = async (m: EquipoMensaje, emoji: string) => {
		setReac(null);
		try {
			await http.prospectsService.equipoMensajeAccion(campusId, m.id, { accion: "reaccion", emoji });
			await cargar(false);
			if (m.id === hiloId) onCambio();
		} catch (e) { setError(errorTexto(e)); }
	};
	const borrar = async (m: EquipoMensaje) => {
		if (!window.confirm("¿Borrar esta respuesta?")) return;
		try {
			await http.prospectsService.equipoMensajeAccion(campusId, m.id, { accion: "borrar" });
			await cargar(false);
			onCambio();
		} catch (e) { setError(errorTexto(e)); }
	};
	const seguir = async () => {
		if (!h) return;
		const si = !h.sigo || h.silenciado;
		try {
			await http.prospectsService.equipoSeguirHilo(campusId, hiloId, si);
			setH({ ...h, sigo: true, silenciado: !si });
		} catch (e) { setError(errorTexto(e)); }
	};
	const elegirMencion = (p: Persona) => {
		men.poner(caja.current, texto, p, setTexto);
		setMenciones((prev) => (prev.some((x) => x.id === p.id) ? prev : [...prev, { id: p.id, nombre: primerNombre(p) }]));
	};

	const fila = (m: EquipoMensaje, seguido: boolean, padre = false) => (
		<div key={m.id} className={"hp-fila" + (seguido ? " seguido" : "") + (padre ? " padre" : "")}>
			{seguido ? <span className="hp-hueco" aria-hidden="true"><time>{hora(m.created_at)}</time></span>
				: <FotoPersona id={m.user_id} nombre={m.author_name} fotos={fotos} onClick={(e) => onPerfil(m.user_id, e)} />}
			<div className="hp-cuerpo">
				{!seguido ? (
					<div className="hp-cab">
						<button type="button" className="hp-nombre" onClick={(e) => onPerfil(m.user_id, e)}>{m.author_name}</button>
						<time title={diaEtiqueta(m.created_at) + " " + hora(m.created_at)}>{padre ? diaEtiqueta(m.created_at) + " · " : ""}{hora(m.created_at)}</time>
						{m.edited_at && !m.deleted_at ? <span className="hp-editado">(editado)</span> : null}
					</div>
				) : null}
				{m.deleted_at ? <p className="apagado"><i>Mensaje borrado</i></p> : (
					<>
						{m.file_tipo ? <MedioMensaje campusId={campusId} m={m} todos={h ? [h.padre, ...h.respuestas] : [m]} /> : null}
						{m.body ? <Texto t={m.body} /> : null}
					</>
				)}
				{m.hilo_tambien && !padre ? <span className="hp-tambien"><WappIcono n="forward" />También en la conversación</span> : null}
				<PastillaReacciones reacciones={m.reacciones} onReaccionar={(e) => reaccionar(m, e)} />
				{!m.deleted_at ? (
					<div className="hp-acciones">
						<button type="button" title="Reaccionar" aria-label="Reaccionar" onClick={() => setReac(reac === m.id ? null : m.id)}><WappIcono n="smile" /></button>
						{resumenTexto(m.body) ? <button type="button" title="Copiar" aria-label="Copiar" onClick={() => copiarTexto(resumenTexto(m.body))}><WappIcono n="copy" /></button> : null}
						{m.user_id === yoId && !padre && !m.sin_borrar ? <button type="button" title="Borrar" aria-label="Borrar" onClick={() => borrar(m)}><WappIcono n="trash" /></button> : null}
					</div>
				) : null}
				{reac === m.id ? (
					<div className="hp-emojis">{EMOJIS.map((e) => <button type="button" key={e} onClick={() => reaccionar(m, e)}>{e}</button>)}</div>
				) : null}
			</div>
		</div>
	);

	let ultimo: EquipoMensaje | null = null;
	return (
		<aside className="hp" aria-label="Hilo">
			<div className="hp-barra">
				<div className="hp-titulo"><b>Hilo</b><span>{donde}</span></div>
				{h ? (
					<button type="button" className={"hp-ico" + (h.sigo && !h.silenciado ? " on" : "")} onClick={seguir}
						title={h.sigo && !h.silenciado ? "Dejar de recibir avisos de este hilo" : "Recibir avisos de este hilo"}
						aria-label={h.sigo && !h.silenciado ? "Dejar de seguir" : "Seguir hilo"}><WappIcono n={h.sigo && !h.silenciado ? "bell" : "bellOff"} /></button>
				) : null}
				<button type="button" className="hp-ico" onClick={onCerrar} aria-label="Cerrar hilo" title="Cerrar (Esc)"><WappIcono n="x" /></button>
			</div>
			<div className="hp-lista" ref={lista}>
				{!h ? <div className="hp-vacio">{error || "Cargando…"}</div> : (
					<>
						{fila(h.padre, false, true)}
						<div className="hp-sep"><span>{h.respuestas.length ? h.respuestas.length + (h.respuestas.length === 1 ? " respuesta" : " respuestas") : "Sin respuestas todavía"}</span></div>
						{h.respuestas.map((m) => {
							const seguido = !!ultimo && ultimo.user_id === m.user_id && Date.parse(m.created_at.replace(" ", "T") + "Z") - Date.parse(ultimo.created_at.replace(" ", "T") + "Z") < 600000;
							ultimo = m;
							return fila(m, seguido);
						})}
					</>
				)}
				<div ref={fin} />
			</div>
			{h && puedoEscribir ? (
				<div className="hp-caja">
					<ListaMenciones sugeridas={men.sugeridas} activa={men.activa} onActiva={men.setActiva} onElegir={elegirMencion} />
					{error ? <div className="aviso error" role="alert">{error}</div> : null}
					<div className="hp-escribir">
						<textarea ref={caja} rows={1} value={texto} placeholder="Responder en el hilo…" aria-label="Responder en el hilo"
							onChange={(e) => { setTexto(e.target.value); men.revisar(e.target.value, e.target.selectionStart); }}
							onKeyDown={(e) => { if (men.tecla(e, elegirMencion)) return; teclaEnter(e, enviar); }} />
						<div className="hp-herr">
							<BotonEmoji compacto onElegir={(em) => insertarEnCursor(caja.current, texto, em, setTexto)} />
							<label className="hp-tambien-check"><input type="checkbox" checked={tambien} onChange={(e) => setTambien(e.target.checked)} />También mandar a la conversación</label>
							<button type="button" className="hp-enviar" disabled={!texto.trim() || enviando} onClick={enviar} aria-label="Responder" title="Responder (Enter)">
								<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12.6 2-12.6 2z" /></svg>
							</button>
						</div>
					</div>
				</div>
			) : null}
		</aside>
	);
};

export default HiloPanel;

/** Vista "Hilos" (como Slack): los hilos que sigo, lo más reciente arriba; los que tienen respuestas nuevas, marcados. */
export const HilosVista: React.FC<{ hilos: import("../../../services/v2/prospects.service").EquipoHiloResumen[] | null; onCerrar: () => void;
	onAbrir: (canalId: number, hiloId: number) => void; onPerfil: (userId: number, e: React.MouseEvent) => void }> = ({ hilos, onCerrar, onAbrir, onPerfil }) => {
	const fotos = useFotos();
	const corto = (body: string | null, tipo: string | null) => resumenTexto(body) || (tipo ? adjuntoCorto(tipo) : "Mensaje");
	return (
		<div className="ventas-cliente hv">
			<div className="hv-cab">
				<b>Hilos</b><span>Las conversaciones en hilo que sigues</span>
				<button type="button" className="hp-ico" onClick={onCerrar} aria-label="Cerrar Hilos"><WappIcono n="x" /></button>
			</div>
			<div className="hv-lista">
				{hilos === null ? <div className="hp-vacio">Cargando…</div>
					: !hilos.length ? (
						<div className="hv-vacio"><WappIcono n="message" /><b>Todavía no sigues ningún hilo</b>
							<span>Pasa el cursor sobre un mensaje y toca «Responder en hilo». Los hilos donde escribas o te mencionen salen aquí.</span></div>
					) : hilos.map((x) => (
						<button type="button" key={x.id} className={"hv-item" + (x.nuevas ? " nuevo" : "")} onClick={() => onAbrir(x.canal_id, x.id)}>
							<span className="hv-donde">{x.tipo === "canal" ? "# " : ""}{x.canal}{x.nuevas ? <span className="badge-n">{x.nuevas}</span> : null}</span>
							<span className="hv-padre">
								<FotoPersona id={x.user_id} nombre={x.autor} fotos={fotos} onClick={(e) => { e.stopPropagation(); onPerfil(x.user_id, e); }} />
								<span><b>{x.autor}</b><span className="hv-txt">{corto(x.body, x.file_tipo)}</span></span>
							</span>
							<span className="hv-pie">
								<span className="eq-hilo-caras" aria-hidden="true">{x.gente.map((u) => <span key={u} className={"eq-hilo-cara tono-" + (Math.abs(u) % 6)}>{fotos[u] ? <img src={fotos[u]} alt="" loading="lazy" /> : null}</span>)}</span>
								<span>{x.respuestas} {x.respuestas === 1 ? "respuesta" : "respuestas"}{x.ultimo_at ? " · última " + cuando(x.ultimo_at) : ""}</span>
								{x.ultima ? <span className="hv-ultima">{x.ultima.autor.split(" ")[0]}: {corto(x.ultima.body, x.ultima.file_tipo)}</span> : null}
							</span>
						</button>
					))}
			</div>
		</div>
	);
};
